import { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Menu,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  Avatar,
} from '@mui/material';
import {
  PeopleAltRounded,
  AssignmentRounded,
  SchoolRounded,
  ClassRounded,
  PollRounded,
  LogoutRounded,
  MenuRounded,
  LightModeRounded,
  DarkModeRounded,
  LanguageRounded,
  ContactsRounded,
  CampaignRounded,
  UnfoldMoreRounded,
  BusinessRounded,
  ViewListRounded,
  ManageAccountsRounded,
  AccountBalanceRounded,
  AccountBalanceWalletRounded,
  TuneRounded,
  VideocamRounded,
  PermMediaRounded,
  WorkspacePremiumRounded,
  RouteRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
  BarChartRounded,
  AnnouncementRounded,
  LibraryBooksRounded,
  AutoStoriesRounded,
  FactCheckRounded,
  RuleRounded,
  ForumRounded,
  InsightsRounded,
  ExpandMoreRounded,
  AdminPanelSettingsRounded,
  RocketLaunchRounded,
  CheckRounded,
  HubRounded,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOutAndReload } from '../../lib/session';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/auth.context';
import { useTenantStore } from '../../stores/tenant.store';
import { useUiStore } from '../../stores/ui.store';
import { useTenantConfig } from '../../hooks/use-tenant-config';
import { tenantInitialLanguage } from '../../lib/tenant-language';
import { usePermissions } from '../../hooks/use-permissions';
import { AFTER_TENANT_SWITCH, ROUTE_GUARDS } from '../../config/permissions.config';
import { platformApi } from '../../lib/api/platform.api';
import { NotificationsBell } from '../notifications/NotificationsBell';
import { AssistantDrawer } from '../assistant/AssistantDrawer';
import { TeachMark } from '../TeachMark';
import { SetupProgressWidget } from '../setup/SetupProgressWidget';
import i18n from '../../i18n';
import { useQueryClient } from '@tanstack/react-query';
import { usePortalViewStore } from '../../stores/portal-view.store';
import { isNavItemVisible } from './nav-visibility';

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 64;

interface NavItem {
  labelKey: string;
  icon: React.ReactNode;
  path: string;
  /** If set, renders a section header ABOVE this item (first item of each group). */
  section?: string;
  /** La ruta con que la GUARDA protege la página, si no es `path`
   *  (ver ROUTE_GUARDS): el menú pregunta por la misma. */
  access?: string;
}

// Each item specifies which paths are permitted via usePermissions().canAccess
// Items with `section` start a new visual group. Order follows the student
// lifecycle (capture → admission → teaching → finance → analytics) up top, with
// set-once configuration and system administration grouped at the bottom.
// `section` is an i18n key (rendered via t()).
// Platform-admin only. Prepended to the nav source when isPlatformAdmin, so it
// never shows for tenant admins (canAccess would otherwise let them through —
// tenantAdmin is also "isAdmin"). The console hosts every super-admin surface
// (overview + seminary requests) via tabs, so it's a single entry.
const PLATFORM_NAV_ITEMS: NavItem[] = [
  {
    section: 'nav.section.platform',
    labelKey: 'nav.platform',
    icon: <AdminPanelSettingsRounded />,
    path: '/plataforma',
  },
];

// Solo para `networkAuditor`. Se antepone por ROL, no por canAccess: canAccess
// devuelve true para cualquier isAdmin, así que un ítem en NAV_ITEMS se lo
// mostraría al admin de CUALQUIER seminario. Mismo motivo por el que
// PLATFORM_NAV_ITEMS vive aparte.
const NETWORK_NAV_ITEMS: NavItem[] = [
  {
    section: 'nav.section.network',
    labelKey: 'nav.network',
    icon: <HubRounded />,
    path: '/red',
  },
];

const NAV_ITEMS: NavItem[] = [
  // ── Gestión (vista transversal: arriba de todo) ───────────────────────
  {
    section: 'nav.section.management',
    labelKey: 'nav.reports',
    icon: <BarChartRounded />,
    path: '/reportes',
  },
  { labelKey: 'nav.people', icon: <PeopleAltRounded />, path: '/people' },
  {
    labelKey: 'nav.students',
    icon: <SchoolRounded />,
    path: '/students',
  },

  // ── Captación (pre-admisión) ───────────────────────────────────────────
  {
    section: 'nav.section.capture',
    labelKey: 'nav.crmContacts',
    icon: <ContactsRounded />,
    path: '/crm/contacts',
  },
  { labelKey: 'nav.crmCampaigns', icon: <CampaignRounded />, path: '/crm/campaigns' },

  // ── Admisión y Matrícula ───────────────────────────────────────────────
  {
    section: 'nav.section.admission',
    labelKey: 'nav.admissions',
    icon: <AssignmentRounded />,
    path: '/admissions',
  },
  { labelKey: 'nav.sections', icon: <ClassRounded />, path: '/sections' },

  // ── Docencia (escritorio diario del docente) ───────────────────────────
  {
    section: 'nav.section.teaching',
    labelKey: 'nav.courseBuilder',
    icon: <AutoStoriesRounded />,
    path: '/curso',
  },
  // "Material de Clase", "Asignaciones" y "Libro de Notas" se fundieron en
  // Curso → tabs (Biblioteca / Asignaciones / Notas). Las rutas /assignments
  // y /grades siguen como redirects a los tabs para links antiguos.
  { labelKey: 'nav.surveys', icon: <PollRounded />, path: '/encuestas' },
  { labelKey: 'nav.standards', icon: <RuleRounded />, path: '/estandares' },
  { labelKey: 'nav.socratic', icon: <ForumRounded />, path: '/dialogo-socratico' },
  { labelKey: 'nav.practiceProgress', icon: <InsightsRounded />, path: '/practica-progreso' },
  {
    labelKey: 'nav.announcements',
    icon: <AnnouncementRounded />,
    path: '/academic/anuncios',
    access: ROUTE_GUARDS.announcementsAdmin,
  },

  // ── Finanzas ───────────────────────────────────────────────────────────
  {
    section: 'nav.section.finance',
    labelKey: 'nav.tuition',
    icon: <AccountBalanceRounded />,
    path: '/tuition',
  },
  { labelKey: 'nav.scholarships', icon: <WorkspacePremiumRounded />, path: '/scholarships' },

  // ── Configuración Académica (se arma una vez) ──────────────────────────
  {
    section: 'nav.section.academicSetup',
    labelKey: 'nav.academic',
    icon: <SchoolRounded />,
    path: '/academic',
  },
  { labelKey: 'nav.caminoTmai', icon: <RouteRounded />, path: '/cap' },
  { labelKey: 'nav.courseTemplates', icon: <ViewListRounded />, path: '/plantillas-syllabus' },
  { labelKey: 'nav.textbookCatalog', icon: <LibraryBooksRounded />, path: '/catalogo-libros' },
  { labelKey: 'nav.formTemplates', icon: <ViewListRounded />, path: '/form-templates' },

  // ── Administración (sistema) ───────────────────────────────────────────
  {
    section: 'nav.section.admin',
    labelKey: 'nav.users',
    icon: <ManageAccountsRounded />,
    path: '/usuarios',
  },
  { labelKey: 'nav.settings', icon: <TuneRounded />, path: '/settings' },
  {
    labelKey: 'nav.zoomIntegration',
    icon: <VideocamRounded />,
    path: '/settings/integrations/zoom',
  },
  {
    labelKey: 'nav.mercadopagoIntegration',
    icon: <AccountBalanceWalletRounded />,
    path: '/settings/integrations/mercadopago',
  },
  { labelKey: 'nav.dataAudit', icon: <FactCheckRounded />, path: '/data-audit' },
  { labelKey: 'nav.crmMedia', icon: <PermMediaRounded />, path: '/crm/media' },
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isPlatformAdmin } = useAuth();
  const { canAccess, permissionsReady, hasRole } = usePermissions();
  const {
    currentTenantId,
    availableTenants,
    setCurrentTenantId,
    platformViewAs,
    setPlatformViewAs,
  } = useTenantStore();
  const {
    colorMode,
    toggleColorMode,
    language,
    setLanguage,
    languageExplicit,
    setDetectedLanguage,
  } = useUiStore();

  // «VER PORTAL» TERMINA AL VOLVER AL PANEL. Si el personal deja el portal de
  // un alumno por cualquier camino —no solo con «Volver a la ficha»—, la vista
  // se cierra acá: sus datos salen de la caché y ninguna llamada del panel
  // sigue llevando el portalViewId (ver lib/portal-view).
  // Solo AL MONTAR el panel —es decir, al volver del portal—: la vista se abre
  // desde la ficha, que vive dentro de este panel, y no hay que cerrarla en el
  // mismo momento en que se crea.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!usePortalViewStore.getState().view) return;
    queryClient.removeQueries();
    usePortalViewStore.getState().exit();
  }, [queryClient]);

  // EL IDIOMA DEL SEMINARIO (M4·D1, para todos): si el usuario no eligió
  // idioma, la app toma el del seminario (`config.locale`). Una elección
  // explícita siempre gana. Queda como idioma DETECTADO, no elegido. Las redes
  // (`config.isNetwork`) siguen como antes: solo con el expediente por
  // credenciales (antes, la bandera `graduation_audit`).
  const tenantConfig = useTenantConfig();
  const initialLanguage = tenantInitialLanguage({
    languageExplicit,
    locale: tenantConfig.locale,
    isNetwork: tenantConfig.isNetwork,
    credentialRecord: tenantConfig.academicRecord.mode === 'credentials',
  });
  useEffect(() => {
    if (!initialLanguage || initialLanguage === i18n.language) return;
    setDetectedLanguage(initialLanguage);
    void i18n.changeLanguage(initialLanguage);
  }, [initialLanguage, setDetectedLanguage]);

  // Platform-admin sees the super-admin section at the top of the nav.
  // Un revisor de red ve SOLO lo de la red: sus rutas no incluyen ninguna
  // pantalla de seminario, así que el resto del menú quedaría vacío igual.
  const navSource = hasRole('networkAuditor')
    ? [...NETWORK_NAV_ITEMS, ...NAV_ITEMS]
    : isPlatformAdmin
      ? [...PLATFORM_NAV_ITEMS, ...NAV_ITEMS]
      : NAV_ITEMS;

  function exitPlatformView() {
    if (!platformViewAs) return;
    void platformApi.logImpersonation(platformViewAs.tenantId, platformViewAs.tenantName, 'stop');
    const home = platformViewAs.homeTenantId;
    setPlatformViewAs(null);
    if (home) setCurrentTenantId(home);
    navigate('/plataforma');
  }

  // Only show nav items the current user has permission to access.
  // Then re-attach section labels to the first *visible* item of each group,
  // so headers always appear even if the original anchor item was filtered out.
  //
  // Mientras los permisos no están resueltos NO se dibuja menú. `canAccess` es
  // fail-closed, así que dibujar en ese momento mostraría un menú vacío que se
  // llena de golpe. La alternativa vieja era peor: mostraba TODOS los ítems y
  // después sacaba los prohibidos, o sea le enseñaba al usuario secciones que no
  // le corresponden.
  const visibleNavItems = (() => {
    // Tampoco antes de saber qué módulos usa el seminario: mostrar un ítem y
    // después esconderlo es justo lo que se quiere evitar.
    if (!permissionsReady || !tenantConfig.settled) return [];
    const filtered = navSource.filter((item) =>
      isNavItemVisible(item, canAccess, (path) => tenantConfig.pathEnabled(path)),
    );
    const seenSections = new Set<string>();
    return filtered.map((item) => {
      const fullIdx = navSource.indexOf(item);
      let belongsToSection: string | undefined;
      for (let i = fullIdx; i >= 0; i--) {
        if (navSource[i]?.section) {
          belongsToSection = navSource[i]!.section;
          break;
        }
      }
      const isFirstOfGroup = !!belongsToSection && !seenSections.has(belongsToSection);
      if (isFirstOfGroup) seenSections.add(belongsToSection!);
      // `section` is set only on the group's first visible item (renders the
      // header); `groupKey` is set on EVERY item so collapsing can hide them all.
      return {
        ...item,
        section: isFirstOfGroup ? belongsToSection : undefined,
        groupKey: belongsToSection,
      };
    });
  })();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Section groups the user has collapsed (by i18n section key). Persisted so
  // the choice survives reloads.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('nav:collapsedGroups');
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set();
    }
  });
  const toggleGroup = (key: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem('nav:collapsedGroups', JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);
  const [tenantAnchor, setTenantAnchor] = useState<null | HTMLElement>(null);

  const currentTenant = availableTenants.find((t) => t.id === currentTenantId);

  const isDark = colorMode === 'dark';

  async function handleLogout() {
    // Recarga: la sesión siguiente no hereda caché ni página (lib/session).
    await signOutAndReload();
  }

  function handleLangChange(lang: 'es' | 'en') {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    setLangAnchor(null);
  }

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Logo + collapse toggle */}
      <Box
        sx={{
          p: sidebarCollapsed ? 1 : 2.5,
          pb: sidebarCollapsed ? 1 : 2,
          transition: 'padding 0.25s',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          justifyContent={sidebarCollapsed ? 'center' : 'flex-start'}
        >
          <Box sx={{ color: 'text.primary', display: 'flex', flexShrink: 0 }}>
            <TeachMark size={34} color="currentColor" />
          </Box>
          {!sidebarCollapsed && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2} noWrap>
                {t('appName')}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {t('appSubtitle')}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>

      <Divider sx={{ mx: sidebarCollapsed ? 1 : 2, mb: 1 }} />

      {/* Navigation — grouped by process */}
      <List
        sx={{
          px: sidebarCollapsed ? 0.5 : 1,
          flex: 1,
          overflowY: 'auto',
          // Auto-hide scrollbar: invisible at rest, reveals on hover or while
          // actively scrolling. Reserves 6px so content doesn't shift.
          scrollbarWidth: 'thin', // Firefox
          scrollbarColor: 'transparent transparent', // Firefox (hidden default)
          transition: 'scrollbar-color 0.2s ease',
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            borderRadius: 3,
            transition: 'background-color 0.25s ease',
          },
          // Reveal thumb when user hovers the sidebar OR the thumb itself.
          '&:hover': {
            scrollbarColor: 'rgba(255,255,255,0.18) transparent',
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(255,255,255,0.18)',
            },
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(255,255,255,0.32)',
          },
        }}
      >
        {visibleNavItems.map((item) => {
          const itemPathname = item.path.split('?')[0]!;

          // Determine if this is the active item.
          // Instead of simple startsWith, we consider it active if no longer prefix matches.
          const isPrefixMatch =
            location.pathname === itemPathname || location.pathname.startsWith(itemPathname + '/');
          const isActive =
            isPrefixMatch &&
            !visibleNavItems.some((otherItem) => {
              const otherPath = otherItem.path.split('?')[0]!;
              return (
                otherItem !== item &&
                (location.pathname === otherPath ||
                  location.pathname.startsWith(otherPath + '/')) &&
                otherPath.length > itemPathname.length
              );
            });

          const groupCollapsed = !!item.groupKey && collapsedGroups.has(item.groupKey);
          // In the mini rail collapsing is disabled (icons always shown).
          const hideItem = !sidebarCollapsed && groupCollapsed;

          return (
            <Box key={item.path}>
              {/* Section header — click to collapse/expand the group */}
              {item.section && !sidebarCollapsed && (
                <Stack
                  direction="row"
                  alignItems="center"
                  onClick={() => item.groupKey && toggleGroup(item.groupKey)}
                  sx={{
                    px: 1.5,
                    pt: 2,
                    pb: 0.5,
                    cursor: 'pointer',
                    borderRadius: 1,
                    '&:hover .nav-section-chevron': { opacity: 1 },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      flex: 1,
                      fontWeight: 700,
                      fontSize: 10,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: isDark ? 'rgba(148,163,184,0.55)' : 'rgba(100,116,139,0.7)',
                      userSelect: 'none',
                    }}
                  >
                    {t(item.section)}
                  </Typography>
                  <ExpandMoreRounded
                    className="nav-section-chevron"
                    sx={{
                      fontSize: 16,
                      opacity: groupCollapsed ? 0.7 : 0.35,
                      color: isDark ? 'rgba(148,163,184,0.7)' : 'rgba(100,116,139,0.8)',
                      transform: groupCollapsed ? 'rotate(-90deg)' : 'none',
                      transition: 'transform 0.2s ease, opacity 0.2s ease',
                    }}
                  />
                </Stack>
              )}
              {item.section && sidebarCollapsed && (
                <Divider sx={{ my: 0.5, mx: 1, opacity: 0.4 }} />
              )}
              {!hideItem && (
                <Tooltip title={sidebarCollapsed ? t(item.labelKey) : ''} placement="right">
                  <ListItemButton
                    onClick={() => {
                      navigate(item.path);
                      setMobileOpen(false);
                    }}
                    sx={{
                      borderRadius: 2,
                      mb: 0.25,
                      justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                      px: sidebarCollapsed ? 1 : 1.5,
                      minHeight: 40,
                      bgcolor: isActive
                        ? isDark
                          ? 'rgba(99,102,241,0.15)'
                          : 'rgba(99,102,241,0.1)'
                        : 'transparent',
                      color: isActive ? 'primary.main' : 'text.secondary',
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: sidebarCollapsed ? 0 : 36,
                        color: 'inherit',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {!sidebarCollapsed && (
                      <ListItemText
                        primary={t(item.labelKey)}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: isActive ? 600 : 400,
                          noWrap: true,
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </List>

      <SetupProgressWidget collapsed={sidebarCollapsed} />

      {/* Permanent entry to the Setup Center / guide — stays reachable even at
          100% configured (the banner + progress widget hide when done).
          Gated like every other item: this button used to bypass canAccess
          entirely, so a network reviewer saw a seminary onboarding link and
          could open the Setup Center inside the network tenant. Its two
          siblings (SetupBanner, SetupProgressWidget) already check isAdmin;
          this one was the outlier. */}
      {canAccess('/empezar') && (
        <Box sx={{ px: sidebarCollapsed ? 1 : 2, pb: 0.5 }}>
          <Tooltip title={sidebarCollapsed ? t('nav.setupGuide') : ''} placement="right">
            <ListItemButton
              onClick={() => {
                navigate('/empezar');
                setMobileOpen(false);
              }}
              sx={{
                borderRadius: 2,
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                px: sidebarCollapsed ? 1 : 1.5,
                minHeight: 40,
                bgcolor:
                  location.pathname === '/empezar'
                    ? isDark
                      ? 'rgba(99,102,241,0.15)'
                      : 'rgba(99,102,241,0.1)'
                    : 'transparent',
                color: location.pathname === '/empezar' ? 'primary.main' : 'text.secondary',
                '&:hover': {
                  bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: sidebarCollapsed ? 0 : 36,
                  color: 'inherit',
                  justifyContent: 'center',
                }}
              >
                <RocketLaunchRounded />
              </ListItemIcon>
              {!sidebarCollapsed && (
                <ListItemText
                  primary={t('nav.setupGuide')}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: location.pathname === '/empezar' ? 600 : 400,
                    noWrap: true,
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </Box>
      )}

      <Divider sx={{ mx: sidebarCollapsed ? 1 : 2 }} />

      {/* User footer */}
      <Box sx={{ p: sidebarCollapsed ? 1 : 2, display: 'flex', justifyContent: 'center' }}>
        {sidebarCollapsed ? (
          <Tooltip title={user?.email ?? ''} placement="right">
            <IconButton size="small" onClick={handleLogout} color="inherit">
              <LogoutRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Stack direction="row" alignItems="center" gap={1.5} width="100%">
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 13 }}>
              {user?.email?.[0]?.toUpperCase() ?? '?'}
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="caption" noWrap color="text.secondary" display="block">
                {user?.email}
              </Typography>
            </Box>
            <Tooltip title={t('actions.logout')}>
              <IconButton size="small" onClick={handleLogout} color="inherit">
                <LogoutRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar (mobile) */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { md: 'none' },
          bgcolor: isDark ? '#0f172a' : '#ffffff',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton edge="start" onClick={() => setMobileOpen(!mobileOpen)}>
            <MenuRounded />
          </IconButton>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            {t('appName')}
          </Typography>
          <AssistantDrawer />
          <NotificationsBell />
          <ThemeLanguageControls
            isDark={isDark}
            language={language}
            toggleColorMode={toggleColorMode}
            langAnchor={langAnchor}
            setLangAnchor={setLangAnchor}
            handleLangChange={handleLangChange}
            t={t}
          />
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{
          width: { md: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH },
          flexShrink: 0,
          transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: isDark ? '#0f172a' : '#ffffff',
              overflowX: 'hidden',
              transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>

        {/* Floating sidebar toggle — pinned to the right edge of the drawer */}
        <Tooltip
          title={sidebarCollapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
          placement="right"
        >
          <Box
            onClick={() => setSidebarCollapsed((v) => !v)}
            sx={{
              display: { xs: 'none', md: 'flex' },
              position: 'fixed',
              top: '50%',
              left: sidebarCollapsed
                ? `${DRAWER_WIDTH_COLLAPSED - 12}px`
                : `${DRAWER_WIDTH - 12}px`,
              transform: 'translateY(-50%)',
              zIndex: 1300,
              width: 24,
              height: 48,
              borderRadius: '0 8px 8px 0',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              bgcolor: isDark ? '#1e293b' : '#f1f5f9',
              border: '1px solid',
              borderLeft: 'none',
              borderColor: 'divider',
              color: 'text.secondary',
              transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1), background-color 0.15s',
              boxShadow: '2px 0 8px rgba(0,0,0,0.12)',
              '&:hover': {
                bgcolor: 'primary.main',
                color: 'white',
                borderColor: 'primary.main',
              },
            }}
          >
            {sidebarCollapsed ? (
              <ChevronRightRounded sx={{ fontSize: 16 }} />
            ) : (
              <ChevronLeftRounded sx={{ fontSize: 16 }} />
            )}
          </Box>
        </Tooltip>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Sin esto, el contenido más ancho de una página (una tabla de 8
          // columnas) estiraba `main` más allá de la ventana y toda la página
          // se desbordaba a la derecha. Con minWidth 0, las tablas se
          // desplazan dentro de su propio contenedor.
          minWidth: 0,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          mt: { xs: '64px', md: 0 },
          transition: 'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* Top bar (desktop) */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            minHeight: 52,
          }}
        >
          {/* Tenant selector — left side */}
          <Box
            onClick={(e) => setTenantAnchor(e.currentTarget)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 1.5,
              py: 0.75,
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              border: '1px solid',
              borderColor: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.12)',
              background: isDark ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.04)',
              '&:hover': {
                borderColor: 'primary.main',
                background: isDark ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.08)',
              },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '7px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'white',
                flexShrink: 0,
                boxShadow: '0 2px 6px rgba(99,102,241,0.35)',
              }}
            >
              <BusinessRounded sx={{ fontSize: 16 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  fontSize: 9,
                  lineHeight: 1,
                  mb: 0.25,
                }}
              >
                {t('nav.organization')}
              </Typography>
              <Typography
                variant="body2"
                noWrap
                sx={{ fontWeight: 600, fontSize: 13, lineHeight: 1.2, maxWidth: 220 }}
              >
                {currentTenant?.name || t('nav.selectOrganization')}
              </Typography>
            </Box>
            <UnfoldMoreRounded
              sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.7, flexShrink: 0 }}
            />
          </Box>

          {/* Tenant menu */}
          <Menu
            anchorEl={tenantAnchor}
            open={Boolean(tenantAnchor)}
            onClose={() => setTenantAnchor(null)}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            PaperProps={{
              elevation: 0,
              sx: {
                width: 280,
                maxHeight: 400,
                mt: 1,
                borderRadius: '12px',
                overflow: 'hidden',
                bgcolor: isDark ? '#1e293b' : '#ffffff',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.1)',
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography
                variant="overline"
                sx={{ fontWeight: 800, color: 'text.secondary', fontSize: 10 }}
              >
                {t('orgSwitcher.title')}
              </Typography>
            </Box>
            <Divider sx={{ opacity: 0.5 }} />
            {availableTenants.length === 0 ? (
              <MenuItem disabled sx={{ py: 2, justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('orgSwitcher.empty')}
                </Typography>
              </MenuItem>
            ) : (
              availableTenants.map((tenant) => (
                <MenuItem
                  key={tenant.id}
                  selected={tenant.id === currentTenantId}
                  onClick={() => {
                    setTenantAnchor(null);
                    if (tenant.id === currentTenantId) return;
                    // A manual switch exits any platform view-as.
                    setPlatformViewAs(null);
                    setCurrentTenantId(tenant.id);
                    // Full reload: auth.context reloads the correct membership
                    // for the new tenant (the selector has no membership data),
                    // and every page re-fetches its tenant-scoped data (pages
                    // load on mount and don't re-fetch on an in-place switch).
                    window.location.assign(AFTER_TENANT_SWITCH);
                  }}
                  sx={{
                    py: 1.25,
                    px: 2,
                    mx: 1,
                    my: 0.5,
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    '&.Mui-selected': {
                      bgcolor: 'rgba(99,102,241,0.12)',
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'rgba(99,102,241,0.18)' },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor:
                          tenant.id === currentTenantId ? 'primary.main' : 'rgba(148,163,184,0.1)',
                        color: tenant.id === currentTenantId ? 'white' : 'text.secondary',
                      }}
                    >
                      {(tenant.name?.[0] || '?').toUpperCase()}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={tenant.name}
                    secondary={tenant.slug}
                    primaryTypographyProps={{
                      fontSize: 13,
                      fontWeight: tenant.id === currentTenantId ? 600 : 500,
                    }}
                    secondaryTypographyProps={{ fontSize: 10 }}
                  />
                </MenuItem>
              ))
            )}
          </Menu>

          {/* Bell + Theme + Language — right side */}
          <Stack direction="row" alignItems="center" gap={0.5}>
            <AssistantDrawer />
            <NotificationsBell />
            <ThemeLanguageControls
              isDark={isDark}
              language={language}
              toggleColorMode={toggleColorMode}
              langAnchor={langAnchor}
              setLangAnchor={setLangAnchor}
              handleLangChange={handleLangChange}
              t={t}
            />
          </Stack>
        </Box>
        {/* Platform-mode banner — visible whenever a platformAdmin is viewing
            another tenant from the console. */}
        {platformViewAs && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              px: 3,
              py: 1,
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
            }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <AdminPanelSettingsRounded fontSize="small" />
              <Typography variant="body2" fontWeight={700}>
                {t('banner.viewing', {
                  ns: 'platform',
                  tenant: platformViewAs.tenantName,
                  defaultValue: `Modo plataforma · viendo ${platformViewAs.tenantName}`,
                })}
              </Typography>
            </Stack>
            <Button
              size="small"
              variant="contained"
              color="inherit"
              onClick={exitPlatformView}
              sx={{ color: 'warning.main', bgcolor: 'common.white' }}
            >
              {t('banner.exit', { ns: 'platform', defaultValue: 'Salir' })}
            </Button>
          </Box>
        )}

        {/* Page content */}
        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 } }}>{children}</Box>
      </Box>
    </Box>
  );
}

// ── Theme + Language Controls ─────────────────────────────────────────────
interface ControlsProps {
  isDark: boolean;
  language: 'es' | 'en';
  toggleColorMode: () => void;
  langAnchor: HTMLElement | null;
  setLangAnchor: (el: HTMLElement | null) => void;
  handleLangChange: (lang: 'es' | 'en') => void;
  t: (key: string) => string;
}

function ThemeLanguageControls({
  isDark,
  language,
  toggleColorMode,
  langAnchor,
  setLangAnchor,
  handleLangChange,
  t,
}: ControlsProps) {
  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Tooltip title={isDark ? t('theme.light') : t('theme.dark')}>
        <IconButton size="small" onClick={toggleColorMode}>
          {isDark ? <LightModeRounded fontSize="small" /> : <DarkModeRounded fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title={t(`language.${language}`)}>
        <IconButton size="small" onClick={(e) => setLangAnchor(e.currentTarget)} sx={{ gap: 0.5 }}>
          <LanguageRounded fontSize="small" />
          <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>
            {language.toUpperCase()}
          </Typography>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={langAnchor}
        open={Boolean(langAnchor)}
        onClose={() => setLangAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {(
          [
            { code: 'es', name: 'Español' },
            { code: 'en', name: 'English' },
          ] as const
        ).map(({ code, name }) => (
          <MenuItem
            key={code}
            selected={language === code}
            onClick={() => handleLangChange(code)}
            sx={{ gap: 1, minWidth: 180 }}
          >
            <ListItemIcon sx={{ minWidth: 24 }}>
              {language === code && <CheckRounded fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={name} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {code.toUpperCase()}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
}
